import {
    faArrowTurnDownRight,
    faLocationDot,
    faPlaneDeparture,
} from '@fortawesome/pro-regular-svg-icons';
import Axios, { type CancelTokenSource } from 'axios';
import { parse } from 'qs';
import { cabinClasses, type CabinClass } from './types';
import FlightPriceLoader from './FlightPriceLoader';
import { ReserveType, VerifyPathError } from '@utils/itinerary';
import { TripSearchForm } from '@search/trip/TripSearchForm';
import { type UrlStringParams } from '@search/types';
import {
    PaxType,
    type PaxCount,
    type ReserveInfo,
    SearchMode,
    type Location,
} from '@utils/types';
import { TripType } from '@utils/trip';
import { type FlightPathData, FlightUrlParams } from '@utils/flight';
import {
    AirportsApiApiAirportsGet,
    CabinClassRequest,
} from '@clients/FlightApi';

export class FlightSearchForm extends TripSearchForm {
    flightCabinClass: CabinClass = cabinClasses[0];
    isCapacityTrackCampaign = false;
    airports: Location[] = [];
    cancelFlightSearch: {
        origin: CancelTokenSource | null;
        destination: CancelTokenSource | null;
    } = {
        origin: null,
        destination: null,
    };

    override get domesticDestinations() {
        return this.airports;
    }

    get domesticAirportCodesMap() {
        return this.airports.reduce(
            (obj, curr) => {
                obj[curr.code] = true;
                return obj;
            },
            {} as Record<string, boolean>
        );
    }

    cabinClassFromCode(code?: string): CabinClassRequest {
        switch (code) {
            case 'Y':
                return CabinClassRequest.Economy;
            case 'C':
                return CabinClassRequest.Business;
            case 'P':
                return CabinClassRequest.EconomyPlus;
            default:
                return CabinClassRequest.All;
        }
    }

    override async verifyViewPath(
        path: string,
        pathData: FlightPathData
    ): Promise<void> {
        const fullRegex =
            /^\/flights\/([a-zA-Z]{3,6})-([a-zA-Z]{3,6})\/(\d{4}-\d{1,2}-\d{1,2})\/?(\d{4}-\d{1,2}-\d{1,2})?\/?(?:(\d)\/(\d)\/(\d))?\/?([YC])?/;

        if (!fullRegex.test(path)) {
            return super.verifyViewPath(path, pathData);
        }
        const params = fullRegex.exec(path)!;
        const newUrl = this.generateViewUrl({
            origin: params[1]!.toUpperCase(),
            destination: params[2]!.toUpperCase(),
            departureDate: params[3],
            returnDate: params[4],
            adultCount: params[5],
            childCount: params[6],
            infantCount: params[7],
            cabinClass: params[8],
        });
        const [url, newQueries] = newUrl.split('?');
        await super.verifyViewPath(url, parse(newQueries) as FlightPathData);
        return Promise.reject(
            new VerifyPathError(
                'Path not valid',
                301,
                true,
                url,
                parse(newQueries) as { [key: string]: string }
            )
        );
    }

    override async loadViewUrlParams(params: FlightUrlParams) {
        await super.loadViewUrlParams(params);
        if (this.isForeign) {
            if (
                !this.foreignOrigins.length ||
                !this.foreignOrigins.some(
                    l => l.code === params.origin.substring(0, 3)
                )
            ) {
                await this.loadForeignAirports({
                    target: 'origin',
                    token: params.origin.substring(0, 3),
                });
                const origin = this.findLocationInLocations(
                    params.origin,
                    this.origins
                );
                if (!origin) {
                    throw new VerifyPathError('origin not found', 404, false);
                }
                this.setOrigin(origin);
            }
            if (
                !this.foreignDestinations.length ||
                !this.foreignDestinations.some(
                    l => l.code === params.destination.substring(0, 3)
                )
            ) {
                await this.loadForeignAirports({
                    target: 'destination',
                    token: params.destination.substring(0, 3),
                });
                const destination = this.findLocationInLocations(
                    params.destination,
                    this.destinations
                );
                if (!destination) {
                    throw new VerifyPathError(
                        'destination not found',
                        404,
                        false
                    );
                }
                this.setDestination(destination);
            }
        }
    }

    override async parsePath(
        pathData: FlightPathData
    ): Promise<FlightUrlParams> {
        await this.searchLocations(pathData.origin);
        return new FlightUrlParams(
            [
                {
                    origin: pathData.origin.toUpperCase(),
                    destination: pathData.destination.toUpperCase(),
                    departureDate: pathData.departureDate
                        ? parseDate(pathData.departureDate)!
                        : dayConfig.gregory().add(1, 'day').toDate(),
                    returnDate: pathData.returnDate
                        ? parseDate(pathData.returnDate)!
                        : undefined,
                },
            ],
            Number(pathData.adultCount) || 1,
            Number(pathData.childCount) || 0,
            Number(pathData.infantCount) || 0,
            this.cabinClassFromCode(pathData.cabinClass)
        );
    }

    override get domesticOrigins(): Location[] {
        return this.airports;
    }

    override foreignOrigins: Location[] = [
        {
            code: 'IKA',
            countryName: 'ایران',
            title: 'تهران',
            subtitle: 'IKA',
        },
    ];

    override swap() {
        super.swap();
        if (this.isForeign) {
            const temp = this.foreignOrigins;
            this.foreignOrigins = this.foreignDestinations;
            this.foreignDestinations = temp;
        }
    }

    handleCapacityTrackCampaign(utmCampaign?: string) {
        if (process.client) {
            this.isCapacityTrackCampaign =
                !!sessionStorage.getItem('utm_campaign_capacity_track') ||
                utmCampaign === 'CapacityTrack';
            if (utmCampaign === 'CapacityTrack') {
                sessionStorage.setItem('utm_campaign_capacity_track', 'true');
            }
        } else {
            this.isCapacityTrackCampaign = utmCampaign === 'CapacityTrack';
        }
    }

    override foreignDestinations = this.foreignOrigins;

    override generateViewUrl(params: UrlStringParams<FlightUrlParams>) {
        const query = new URLSearchParams();
        if (params.departureDate) {
            query.append('departureDate', params.departureDate);
        }
        if (params.returnDate) {
            query.append('returnDate', params.returnDate);
        }
        if (params.adultCount && params.adultCount !== '1') {
            query.append('adultCount', params.adultCount);
        }
        if (params.childCount && params.childCount !== '0') {
            query.append('childCount', params.childCount);
        }
        if (params.infantCount && params.infantCount !== '0') {
            query.append('infantCount', params.infantCount);
        }
        if (params.cabinClass) {
            query.append('cabinClass', params.cabinClass);
        }
        const q = query.toString();
        return `/flights/${params.origin}-${params.destination}${
            q ? `?${q}` : ''
        }`;
    }

    override getPriceLoader(
        origin: Location,
        destination: Location,
        tripType: TripType
    ): FlightPriceLoader {
        return new FlightPriceLoader({
            tripType,
            from: origin.code,
            to: destination.code,
            adultCount: this.adultCount,
            childCount: this.childCount,
            infantCount: this.infantCount,
        });
    }

    override initPaxCounts: PaxCount[] = [
        {
            code: PaxType.Adult,
            title: 'بزرگسال',
            subtitle: 'بزرگتر از ۱۲ سال',
            count: 1,
        },
        {
            code: PaxType.Child,
            title: 'کودک',
            subtitle: 'بین ۲ الی ۱۲ سال',
            count: 0,
        },
        {
            code: PaxType.Infant,
            title: 'نوزاد',
            subtitle: 'کوچکتر از ۲ سال',
            count: 0,
        },
    ];

    get maxCapacity() {
        return 9 - this.childCount - this.infantCount;
    }

    override get capacityInfo(): {
        [key in PaxType]?: {
            count: number;
            min: number;
            max: number;
        };
    } {
        const infantGap = this.adultCount * 3 - this.childCount;

        return {
            [PaxType.Adult]: {
                count: this.adultCount,
                min: 1,
                max: this.maxCapacity,
            },
            [PaxType.Child]: {
                count: this.childCount,
                min: 0,
                max: this.adultCount * 3 - this.infantCount,
            },
            [PaxType.Infant]: {
                count: this.infantCount,
                min: 0,
                max: infantGap > this.adultCount ? this.adultCount : infantGap,
            },
        };
    }

    override updatePaxCount(params: { type: PaxType; count: number }) {
        const paxCount = this.capacityInfo[params.type];
        const initPaxCount = this.initPaxCounts.find(
            pax => pax.code === params.type
        );
        if (
            !initPaxCount ||
            !paxCount ||
            params.count > paxCount.max ||
            params.count < paxCount.min
        ) {
            if (
                params.type === PaxType.Child ||
                params.type === PaxType.Infant
            ) {
                throw new Error(
                    'به ازای هر بزرگسال 3 کودک یا 2 کودک و یک نوزاد مجاز است.'
                );
            } else if (params.type === PaxType.Adult) {
                throw new Error('حداکثر تعداد مسافران 9 نفر است');
            }
            return;
        }

        if (
            params.type === PaxType.Adult &&
            paxCount.count > params.count &&
            (this.childCount + this.infantCount > (this.adultCount - 1) * 3 ||
                this.infantCount === this.adultCount)
        ) {
            throw new Error(
                'در صورت کاهش بزرگسال، شرط سقف تعداد کودک یا نوزاد رعایت نمی شود'
            );
        }

        if (
            this.adultCount + this.childCount + this.infantCount === 9 &&
            params.count > paxCount.count
        ) {
            throw new Error('حداکثر تعداد مسافران 9 نفر است');
        }
        initPaxCount.count = params.count;
    }

    override popularForeignOrigins: Location[] = [
        {
            code: 'IKA',
            countryName: 'ایران',
            title: 'تهران',
            subtitle: 'IKA',
        },
        {
            code: 'ISTALL',
            countryName: 'ترکیه',
            title: 'استانبول',
            subtitle: 'همه فرودگاه‌ها',
        },
        {
            code: 'NJF',
            countryName: 'عراق',
            title: 'نجف',
            subtitle: 'NJF',
        },
        {
            code: 'TBS',
            countryName: 'گرجستان',
            title: 'تفلیس',
            subtitle: 'TBS',
        },
    ];

    override popularForeignDestinations = this.popularForeignOrigins;

    override popularDomesticOrigins = [
        { code: 'THR', title: 'تهران', subtitle: 'فرودگاه مهرآباد' },
        { code: 'MHD', title: 'مشهد', subtitle: 'فرودگاه شهید هاشمی‌نژاد' },
        { code: 'SYZ', title: 'شیراز', subtitle: 'فرودگاه شهید دستغیب' },
        { code: 'KIH', title: 'کیش', subtitle: 'فرودگاه کیش' },
        { code: 'AWZ', title: 'اهواز', subtitle: 'فرودگاه اهواز' },
    ];

    override popularDomesticDestinations = this.popularDomesticOrigins;

    override get reserveInfo(): ReserveInfo {
        return {
            type: ReserveType.Flight,
            title: 'هواپیما',
            nameE: 'هواپیمایی',
            icon: faPlaneDeparture,
        };
    }

    override get filterSearchLocations() {
        return this.isDomestic;
    }

    override get viewUrl() {
        const cabinClass = !this.isDomestic
            ? `/${this.flightCabinClass?.code}`
            : '';
        return this.generateViewUrl({
            origin: this.origin?.code,
            destination: this.destination?.code,
            departureDate: this.formattedDepartureDate,
            returnDate:
                this.tripType === TripType.RoundTrip
                    ? this.formattedReturnDate
                    : undefined,
            adultCount: this.adultCount.toString(),
            childCount: this.childCount.toString(),
            infantCount: this.infantCount.toString(),
            cabinClass,
        });
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination'; mode: SearchMode } = {
            mode: SearchMode.Domestic,
            target: 'origin',
        }
    ) {
        if (this.isDomestic || options.mode === SearchMode.Domestic) {
            if (!this.airports.length) {
                this.isLoadingLocations = true;
                this.airports = (
                    await import('~/lib/static/domestic_airports.js')
                ).default;
                this.isLoadingLocations = false;
            }
        } else {
            await this.loadForeignAirports({
                token: searchKeyword,
                target: options.target,
            });
        }
        await super.searchLocations(searchKeyword, options);
    }

    async loadForeignAirports({
        target,
        token,
    }: {
        token: string;
        target: 'origin' | 'destination';
    }) {
        const prevCancel = this.cancelFlightSearch[target];
        if (typeof prevCancel?.cancel === 'function') {
            prevCancel.cancel();
        }
        try {
            this.isLoadingLocations = true;
            if (token.trim()) {
                const newCancel = Axios.CancelToken.source();
                this.cancelFlightSearch = {
                    origin: target === 'origin' ? newCancel : null,
                    destination: target === 'destination' ? newCancel : null,
                };
                const { data } = await AirportsApiApiAirportsGet(
                    {
                        term: token,
                    },
                    {
                        cancelToken: newCancel.token,
                    }
                );

                const options: Location[] = data.map(city => ({
                    code: city.Code || '',
                    title: city.PersianTitle || '',
                    subtitle: city.Airports?.length
                        ? 'همه فرودگاه‌ها'
                        : city.Code || '',
                    countryName: city.CountryPersianTitle || '',
                    icon: faLocationDot,
                    subLocations: city.Airports?.map((ap: any) => ({
                        code: ap.Code,
                        title: ap.CityPersianTitle,
                        subtitle: `فرودگاه ${ap.Title}`,
                        countryName: ap.Code,
                        icon: faArrowTurnDownRight,
                    })),
                }));
                switch (target) {
                    case 'origin':
                        this.foreignOrigins = options;
                        break;
                    case 'destination':
                        this.foreignDestinations = options;
                        break;
                }
            } else {
                switch (target) {
                    case 'origin':
                        this.foreignOrigins = [];
                        break;
                    case 'destination':
                        this.foreignDestinations = [];
                        break;
                }
            }
        } catch (error) {
            if (Axios.isCancel(error)) {
                return;
            }
            throw error;
        } finally {
            this.isLoadingLocations = false;
        }
    }

    override async loadLandingSearchParams(destinationCode: string) {
        let origin: string;
        if (this.isDomestic) {
            origin = destinationCode === 'THR' ? 'IFN' : 'THR';
        } else {
            origin = 'IKA';
        }
        await this.searchLocations(origin, {
            target: 'origin',
            mode: this.searchMode,
        });
        this.setOriginBaseOnCode(origin);
        if (this.isForeign) {
            await this.loadForeignAirports({
                target: 'destination',
                token: destinationCode,
            });
        }
        await this.searchLocations(destinationCode, {
            target: 'destination',
            mode: this.searchMode,
        });
        this.setDestinationBaseOnCode(destinationCode);
    }
}
